<template>
    <Modal title="Edit Division" ref="modalRef">
        <template v-slot:body>
            <div>
                <div class="company-name margin-b-1">
                    <span>{{ props.company.name }}</span> 
                    <span v-if="settingsStore.debugMode"> ({{ props.company.id }})</span>
                </div>
                <div>{{ props.company.address1 }}</div>
                <div v-if="!!props.company.address2">{{ props.company.address2 }}</div>
                <div class="margin-b-1">
                    {{ props.company.city }}, {{ props.company.state }} {{ props.company.zip }}
                </div>
                <div v-if="props.allowBlock && props.company.projectCount > 0">
                    This division is in use on {{ props.company.projectCount }} project{{ props.company.projectCount > 1 ? 's' : '' }}.
                </div>
                <div v-else-if="props.allowBlock && props.company.projectCount === 0">
                    This division is not in use any on projects.
                </div>
            </div>
            <div class="margin-t-1">
                <div class="margin-b-2">{{ props.company.hasOverride ? 'Updated division notes' : 'Division notes' }}:</div>
                <textarea v-model="newNotes" class="parent-notes-text"></textarea>
            </div>
            <div v-if="props.company.hasOverride && props.company.origNotes !== props.company.description" class="margin-t-1">
                <div class="margin-b-2">Division notes prior to modification:</div>
                <textarea :value="props.company.origNotes" disabled class="parent-notes-text"></textarea>
            </div>
            <div v-if="props.company.hasOverride" class="margin-t-1">
                <div class="margin-b-2">Current R2 notes:</div>
                <textarea :value="props.company.description" disabled class="parent-notes-text"></textarea>
            </div>
        </template>
        <template v-slot:footer>
            <div class="edit-group-footer">
                <div>
                    <input
                        type="button"
                        v-if="props.allowBlock && !props.company.migrationBlocked"
                        value="Block Migration"
                        class="button button-danger button-margin"
                        :disabled="company.projectCount"
                        @click="confirmDelete()"
                    />
                    <input
                        v-if="props.allowBlock && props.company.migrationBlocked"
                        type="button"
                        value="Unblock"
                        class="button button-secondary button-margin"
                        @click="confirmUndelete()"
                    />
                    <input
                        v-if="props.allowHide && !props.company.hidden"
                        type="button"
                        value="Hide"
                        class="button button-margin"
                        @click="confirmHide()"
                    />
                    <input
                        v-if="props.allowHide && props.company.hidden"
                        type="button"
                        value="Unhide"
                        class="button button-secondary button-margin"
                        @click="confirmUnhide()"
                    />
                    <input
                        v-if="props.company.hasOverride"
                        type="button"
                        value="Revert Notes"
                        class="revert-button button button-secondary"
                        @click="revertNotes()"
                    />
                </div>
                <div>
                    <input class="button button-secondary button-margin" type="button" value="Cancel" @click="cancel()" />
                    <input type="button" value="Save" class="button" @click="save()" />
                </div>
            </div>
        </template>
    </Modal>
</template>

<script setup>
    import { ref, inject, defineProps, defineEmits, onBeforeMount } from 'vue';
    import { Modal as ModalLib, promptModal } from 'jenesius-vue-modal';
    import { useCompanyStore } from '@/stores/CompanyStore';
    import { useSettingsStore } from '@/stores/SettingsStore';
    import Modal from './Modal.vue';
    import Confirm from './Confirm.vue';
    import Error from '@/components/Error.vue';

    const props = defineProps({
        company: {
            type: Object,
            required: true
        },

        allowHide: {
            type: Boolean,
            default: true
        },

        allowBlock: {
            type: Boolean,
            default: true
        }
    });

    const emit = defineEmits([ModalLib.EVENT_PROMPT]);
    const api = inject('api');
    const modalRef = ref();
    const companyStore = useCompanyStore();
    const settingsStore = useSettingsStore();

    let newNotes;

    onBeforeMount(() => {
        let notes = '';

        if (props.company.hasOverride) {
            notes = props.company.overrideNotes.slice(0);
        }
        else if (props.company.description !== null) {
            notes = props.company.description.slice(0);
        }

        newNotes = ref(notes);
    });

    async function save() {
        modalRef.value.showActivityIndicator = true;

        try {
            await companyStore.updateCompanyNotes(props.company.id, newNotes.value);
            emit(ModalLib.EVENT_PROMPT, true);
        }
        catch (err) {
            promptModal(Error, { message: 'An error occurred while saving company', details: err.stack });
        }
        finally {
            modalRef.value.showActivityIndicator = false;
        }
    }

    function cancel() {
        emit(ModalLib.EVENT_PROMPT, false);
    }

    async function confirmDelete() {
        const data = {
            title: 'Block Company Migration',
            text: 'Blocking the migration of a company means it will not be abailable for use in D2. Are you sure?',
            confirmBtnText: 'Block',
            dangerous: true
        };

        const res = await promptModal(Confirm,  data);

        if (res) {
            try {
                await api.doNotMigrateCompany(props.company.id);
                emit(ModalLib.EVENT_PROMPT, true);
            }
            catch (err) {
                promptModal(Error, { message: 'An error occurred while blocking migration of company', details: err.stack });
            }
        }
    }

    async function confirmHide() {
        const data = {
            title: 'Hide Company',
            text: 'Are you sure you want to hide this company? It will still be  migrated to D2 but it will no longer ' +
                'show up in the list of companies to be grouped in this app.',
            confirmBtnText: 'Hide'
        };

        const res = await promptModal(Confirm,  data);

        if (res) {
            try {
                await api.hideCompany(props.company.id);
                emit(ModalLib.EVENT_PROMPT, true);
            }
            catch (err) {
                promptModal(Error, { message: 'An error occurred while hiding company', details: err.stack });
            }
        }
    }

    async function confirmUnhide() {
        const data = {
            title: 'Unhide Company',
            text: 'Are you sure you want to unhide this company?',
            confirmBtnText: 'Unhide'
        };

        const res = await promptModal(Confirm,  data);

        if (res) {
            try {
                await api.unhideCompany(props.company.id);
                emit(ModalLib.EVENT_PROMPT, true);
            }
            catch (err) {
                promptModal(Error, { message: 'An error occurred while unhiding company', details: err.stack });
            }
        }
    }

    async function confirmUndelete() {
        const data = {
            title: 'Unblock Company',
            text: 'Unblocking this company means it will be migrated from R2 into D2. Are you sure?',
            confirmBtnText: 'Unblock'
        };

        const res = await promptModal(Confirm,  data);

        if (res) {
            try {
                await api.migrateCompany(props.company.id);
                emit(ModalLib.EVENT_PROMPT, true);
            }
            catch (err) {
                promptModal(Error, { message: 'An error occurred while unblocking migration of company', details: err.stack });
            }
        }
    }

    function revertNotes() {
        newNotes.value = props.company.description;
    }
</script>

<style scoped>
    .company-name {
        font-size: 20px;
    }

    .parent-notes-text {
        width: 100%;
        height: 200px;
        resize: none;
    }

    .edit-group-footer {
        flex: 1;
        display: flex;
        justify-content: space-between;
    }

    .button-margin {
        margin-right: 10px;
    }

    .button:disabled {
        background-color: gray;
        border-color: gray;
    }
</style>
@/stores/CompanyStore
